import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Layout from '../components/Layout';
import { api } from '../api';

function formatDate(value) {
  if (!value) return 'Not scanned yet';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? 'Unknown' : date.toLocaleString();
}

const previewLoads = new Map();
let previewCounter = 0;

function loadPreviewFont(familyName) {
  if (!previewLoads.has(familyName)) {
    const promise = api.getFontFile(familyName).then(async (result) => {
      const binary = window.atob(result.data);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);

      previewCounter += 1;
      const name = `fm-preview-${previewCounter}`;
      const face = new window.FontFace(name, bytes.buffer);
      await face.load();
      document.fonts.add(face);
      return name;
    });

    previewLoads.set(familyName, promise);
    promise.catch(() => previewLoads.delete(familyName));
  }

  return previewLoads.get(familyName);
}

const TABS = [
  ['all', 'All'],
  ['enabled', 'Enabled'],
  ['disabled', 'Disabled'],
  ['latin', 'Latin'],
  ['hindi', 'Hindi'],
  ['arabic', 'Arabic / Urdu'],
];

function matchesTab(family, tab) {
  if (tab === 'enabled') return family.enabled;
  if (tab === 'disabled') return !family.enabled;
  if (tab === 'latin') return family.scripts.latin;
  if (tab === 'hindi') return family.scripts.devanagari;
  if (tab === 'arabic') return family.scripts.arabic;
  return true;
}

function FamilyRow({ family, busy, onToggle }) {
  const rowRef = useRef(null);
  const [visible, setVisible] = useState(false);
  const [fontName, setFontName] = useState('');
  const [previewError, setPreviewError] = useState('');

  useEffect(() => {
    const node = rowRef.current;

    if (!node || typeof IntersectionObserver === 'undefined') {
      setVisible(true);
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: '300px' }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!visible || !family.previewable) return undefined;

    let cancelled = false;

    loadPreviewFont(family.family)
      .then((name) => {
        if (!cancelled) setFontName(name);
      })
      .catch((err) => {
        if (!cancelled) setPreviewError(err.message || 'Preview unavailable.');
      });

    return () => {
      cancelled = true;
    };
  }, [visible, family.family, family.previewable]);

  return (
    <div
      ref={rowRef}
      className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between"
    >
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-medium text-gray-900">{family.family}</span>
          {family.fixed && (
            <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">
              Fixed Arabic font
            </span>
          )}
        </div>
        <p className="mt-1 text-xs text-gray-500">
          {family.fontCount} face{family.fontCount === 1 ? '' : 's'}
          {' · '}
          {[
            family.scripts.latin && 'Latin',
            family.scripts.devanagari && 'Devanagari',
            family.scripts.arabic && 'Arabic / Urdu',
          ].filter(Boolean).join(' · ') || 'Coverage not identified'}
        </p>

        {!family.previewable ? (
          <p className="mt-2 text-xs text-gray-400">No TrueType file available to preview.</p>
        ) : fontName ? (
          <div
            className="mt-2 space-y-1 break-words text-xl leading-snug text-gray-900"
            style={{ fontFamily: `"${fontName}", sans-serif` }}
          >
            {family.samples.map((line) => (
              <div key={line} dir="auto" style={{ textAlign: 'left' }}>{line}</div>
            ))}
          </div>
        ) : previewError ? (
          <p className="mt-2 text-xs text-gray-400">{previewError}</p>
        ) : (
          <p className="mt-2 text-xs text-gray-400">
            {visible ? 'Loading preview…' : ' '}
          </p>
        )}
      </div>

      <label className="flex shrink-0 items-center gap-3 text-sm">
        <span className={family.enabled ? 'text-gray-700' : 'text-gray-500'}>
          {family.fixed ? 'Required' : family.enabled ? 'Enabled' : 'Disabled'}
        </span>
        <input
          type="checkbox"
          checked={family.enabled}
          disabled={family.fixed || busy}
          onChange={() => onToggle(family)}
          aria-label={`${family.enabled ? 'Disable' : 'Enable'} ${family.family}`}
          className="h-4 w-4 rounded border-gray-300 text-brand-purple focus:ring-brand-purple disabled:cursor-not-allowed"
        />
      </label>
    </div>
  );
}

export default function FontManager() {
  const [inventory, setInventory] = useState(null);
  const [search, setSearch] = useState('');
  const [tab, setTab] = useState('all');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [savingFamily, setSavingFamily] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const loadInventory = useCallback(async () => {
    setError('');
    try {
      setInventory(await api.getFonts());
    } catch (err) {
      setError(err.message || 'Could not load font inventory.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadInventory();
  }, [loadInventory]);

  const families = useMemo(() => {
    const query = search.trim().toLowerCase();
    return (inventory?.families || []).filter(
      (family) =>
        family.family.toLowerCase().includes(query) && matchesTab(family, tab)
    );
  }, [inventory, search, tab]);

  const tabCounts = useMemo(() => {
    const counts = {};
    TABS.forEach(([key]) => {
      counts[key] = (inventory?.families || []).filter((family) => matchesTab(family, key)).length;
    });
    return counts;
  }, [inventory]);

  const otherScriptFamilies = useMemo(
    () =>
      (inventory?.families || []).filter(
        (family) =>
          !family.fixed &&
          family.enabled &&
          !family.scripts.latin &&
          !family.scripts.devanagari &&
          !family.scripts.arabic
      ),
    [inventory]
  );

  const saveDisabledFamilies = async (disabledFamilies, familyName) => {
    setSavingFamily(familyName);
    setError('');
    setNotice('');

    try {
      const result = await api.saveFontSettings(disabledFamilies);
      setInventory((current) => current ? {
        ...current,
        settings: result.settings,
        families: current.families.map((family) => ({
          ...family,
          enabled: !result.settings.disabledFamilies.includes(family.family),
        })),
      } : current);
      setNotice('Font settings saved.');
    } catch (err) {
      setError(err.message || 'Could not save font settings.');
    } finally {
      setSavingFamily('');
    }
  };

  const toggleFamily = (family) => {
    if (family.fixed || savingFamily) return;

    const disabled = new Set(inventory?.settings?.disabledFamilies || []);
    if (family.enabled) disabled.add(family.family);
    else disabled.delete(family.family);

    saveDisabledFamilies([...disabled], family.family);
  };

  const hideOtherScripts = () => {
    if (savingFamily || !otherScriptFamilies.length) return;

    const message =
      `Disable ${otherScriptFamilies.length} families that support none of Latin, Hindi or Arabic/Urdu? ` +
      'You can re-enable any of them later.';
    if (!window.confirm(message)) return;

    const disabled = new Set(inventory?.settings?.disabledFamilies || []);
    otherScriptFamilies.forEach((family) => disabled.add(family.family));
    saveDisabledFamilies([...disabled], 'bulk');
  };

  const enableAll = () => {
    if (savingFamily) return;
    if (!window.confirm('Enable every font family again?')) return;
    saveDisabledFamilies([], 'bulk');
  };

  const refresh = async () => {
    setRefreshing(true);
    setError('');
    setNotice('');

    try {
      const result = await api.refreshFonts();
      await loadInventory();
      setNotice(`Inventory refreshed: ${result.fontCount.toLocaleString()} font files found.`);
    } catch (err) {
      setError(err.message || 'Font inventory refresh failed.');
    } finally {
      setRefreshing(false);
    }
  };

  const enabledCount = (inventory?.families || []).filter((family) => family.enabled).length;

  return (
    <Layout>
      <div className="mx-auto max-w-7xl space-y-6">
        <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Font Manager</h1>
            <p className="mt-1 text-sm text-gray-600">
              Manage the shared font inventory used by creative generators.
            </p>
          </div>
          <button
            type="button"
            onClick={refresh}
            disabled={refreshing || loading || Boolean(savingFamily)}
            className="rounded-lg bg-brand-purple px-4 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {refreshing ? 'Refreshing fonts…' : 'Refresh inventory'}
          </button>
        </div>

        {error && <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div>}
        {notice && <div role="status" className="rounded-lg border border-green-200 bg-green-50 p-3 text-sm text-green-800">{notice}</div>}

        {loading ? (
          <div className="rounded-xl border border-gray-200 bg-white p-8 text-gray-600">Loading font inventory…</div>
        ) : inventory ? (
          <>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
              {[
                ['Font files', inventory.fontCount.toLocaleString()],
                ['Font families', inventory.familyCount.toLocaleString()],
                ['Enabled families', enabledCount.toLocaleString()],
                ['Refresh interval', `${Math.floor(inventory.ttlMs / 86400000)} days`],
              ].map(([label, value]) => (
                <div key={label} className="rounded-xl border border-gray-200 bg-white p-5">
                  <p className="text-sm text-gray-500">{label}</p>
                  <p className="mt-2 text-2xl font-bold text-gray-900">{value}</p>
                </div>
              ))}
            </div>

            <div className="rounded-xl border border-gray-200 bg-white p-5">
              <h2 className="font-semibold text-gray-900">Inventory cache</h2>
              <p className="mt-1 text-sm text-gray-600">Last scanned: {formatDate(inventory.generatedAt)}</p>
              <p className="mt-2 text-sm text-gray-500">
                The inventory is cached for 30 days. New families are enabled by default.
              </p>
            </div>

            <div className="rounded-xl border border-gray-200 bg-white">
              <div className="space-y-4 border-b border-gray-200 p-5">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <h2 className="font-semibold text-gray-900">Font families</h2>
                    <p className="mt-1 text-sm text-gray-600">Disabled families are excluded from automatic selection.</p>
                  </div>
                  <input
                    type="search"
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    placeholder="Search font families…"
                    aria-label="Search font families"
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm sm:max-w-xs"
                  />
                </div>

                <div className="flex flex-wrap items-center gap-2">
                  {TABS.map(([key, label]) => (
                    <button
                      key={key}
                      type="button"
                      onClick={() => setTab(key)}
                      className={`rounded-full border px-3 py-1 text-xs font-medium ${
                        tab === key
                          ? 'border-brand-purple bg-brand-purple text-white'
                          : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      {label} ({tabCounts[key]})
                    </button>
                  ))}
                  <span className="mx-1 hidden h-4 border-l border-gray-300 sm:inline-block" />
                  <button
                    type="button"
                    onClick={hideOtherScripts}
                    disabled={Boolean(savingFamily) || !otherScriptFamilies.length}
                    className="rounded-lg border border-gray-300 px-3 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Hide fonts without Latin, Hindi or Arabic ({otherScriptFamilies.length})
                  </button>
                  <button
                    type="button"
                    onClick={enableAll}
                    disabled={Boolean(savingFamily)}
                    className="rounded-lg border border-gray-300 px-3 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Enable all
                  </button>
                </div>
              </div>

              <div className="divide-y divide-gray-100">
                {families.map((family) => (
                  <FamilyRow
                    key={family.family}
                    family={family}
                    busy={Boolean(savingFamily)}
                    onToggle={toggleFamily}
                  />
                ))}
                {!families.length && <p className="p-8 text-center text-sm text-gray-500">No font families match your search.</p>}
              </div>

              <div className="border-t border-gray-200 p-4 text-xs text-gray-500">
                Showing {families.length} of {inventory.familyCount} families. Arabic selection is fixed to AlKanz.
              </div>
            </div>
          </>
        ) : null}
      </div>
    </Layout>
  );
}
